import * as React from 'react';
import {
    createContext,
    CSSProperties,
    forwardRef,
    ReactNode,
    RefObject,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { mergeRefs } from 'utils/mergeRefs';

type VisibilityCallback = (isVisible: boolean) => void;

export type VisibilityContext = Readonly<{
    registerElement: (elem: Element, callback: VisibilityCallback) => () => void;
}>;

const Context = createContext<VisibilityContext | undefined>(undefined);

export type VisibilityProviderProps = Readonly<{
    children: ReactNode;
    style?: CSSProperties;
    className?: string;
}>;

export const VisibilityProvider = forwardRef<HTMLDivElement, VisibilityProviderProps>(
    ({ children, style, className }, ref) => {
        const observerRef = useRef<IntersectionObserver>();
        const rootRef = useRef<HTMLDivElement>(null);
        const mergedRef = useMemo(() => mergeRefs(rootRef, ref), [ref]);
        const callbacks = useMemo(() => new Map<Element, VisibilityCallback>(), []);
        const [value, setValue] = useState<VisibilityContext>();

        useEffect(() => {
            if (!observerRef.current) {
                observerRef.current = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            const callback = callbacks.get(entry.target);
                            if (callback) {
                                callback(entry.isIntersecting);
                            }
                        });
                    },
                    {
                        root: rootRef.current,
                        rootMargin: '0px',
                    }
                );

                setValue({
                    registerElement: (elem, callback) => {
                        if (!observerRef.current) {
                            throw new Error('Observer not initialized');
                        }
                        callbacks.set(elem, callback);
                        observerRef.current.observe(elem);

                        return () => callbacks.delete(elem);
                    },
                });
            }

            return () => {
                observerRef.current?.disconnect();
                observerRef.current = undefined;
            };
        }, [callbacks]);

        if (!value) {
            return <></>;
        }

        return (
            <Context.Provider value={value}>
                <div ref={mergedRef} style={style} className={className}>
                    {children}
                </div>
            </Context.Provider>
        );
    }
);

export function useIsVisible<TElem extends Element>(elementRef: RefObject<TElem>) {
    const ctx = useContext(Context);
    const [isVisible, setIsVisible] = useState(false);

    if (!ctx) {
        throw new Error('Missing <VisibilityProvider>');
    }

    useEffect(() => {
        if (!elementRef.current) {
            throw new Error('Missing element');
        }
        return ctx.registerElement(elementRef.current, setIsVisible);
    }, [ctx, elementRef]);

    return isVisible;
}

export function wrapWithVisibilityProvider<TProps extends object>(
    ComponentToWrap: React.ComponentType<TProps>
) {
    return (props: TProps) => (
        <VisibilityProvider>
            <ComponentToWrap {...props} />
        </VisibilityProvider>
    );
}
